import { ChevronDown, ChevronUp, Headphones, MessageCircleIcon, Mic, MicOff, Monitor, MonitorOff, Phone, SwitchCamera, Video, VideoOff, Volume2 } from "lucide-react";
import { Button } from "../../ui/button";
import { useEffect, useRef, useState } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../../ui/alert-dialog";

interface ControlsProps {
  onToggleMic: () => void;
  onToggleVideo: () => void;
  onEndCall: () => void;
  onToggleScreenShare: () => void;
  onOpenChat: () => void;
  isMicOn: boolean;
  isVideoOn: boolean;
  isScreenSharing: boolean;
  unreadMessageCount: number;
  selectedMic: string;
  selectedCamera: string;
  onDeviceChange: (deviceId: string, type: "audio" | "video") => void;
  onFacingModeChange?: (facingMode: "user" | "environment") => void;
}

const getMicIconFromLabel = (label: string): React.ReactNode => {
  const normalizedLabel = label.toLowerCase();
  if (normalizedLabel.includes("headset") || normalizedLabel.includes("airpods") || normalizedLabel.includes("earbuds") || normalizedLabel.includes("headphones")) {
    return <Headphones className="h-6 w-6" />;
  }
  if (normalizedLabel.includes("speaker") || normalizedLabel.includes("output")) {
    return <Volume2 className="h-6 w-6" />;
  }
  return <Mic className="h-6 w-6" />;
};
// Component containing all the control buttons and device selection dropdowns.
const CallControlsButton: React.FC<ControlsProps> = ({
  onToggleMic,
  onToggleVideo,
  onEndCall,
  onToggleScreenShare,
  onOpenChat,
  isMicOn,
  isVideoOn,
  isScreenSharing,
  unreadMessageCount,
  selectedMic,
  selectedCamera,
  onDeviceChange,
  onFacingModeChange,
}) => {
  const micDropdownRef = useRef<HTMLDivElement>(null);
  const cameraDropdownRef = useRef<HTMLDivElement>(null);
  const [cameraCustomDropdownOpen, setCameraCustomDropdownOpen] = useState(false);
  const [micCustomDropdownOpen, setMicCustomDropdownOpen] = useState(false);
  const [selectedMicIcon, setSelectedMicIcon] = useState<React.ReactNode>(<Mic className="h-6 w-6" />);

  const isMobile = /Mobi/i.test(navigator.userAgent);
  const [currentFacingMode, setCurrentFacingMode] = useState<"user" | "environment">("user");
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);

  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const tempStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: true,
        });
        tempStream.getTracks().forEach((track) => track.stop());

        const deviceList = await navigator.mediaDevices.enumerateDevices();
        setDevices(deviceList);
      } catch (err) {
        console.error("Failed to get media permissions or enumerate devices:", err);
        navigator.mediaDevices
          .enumerateDevices()
          .then((deviceList) => {
            setDevices(deviceList);
          })
          .catch((err) =>
            console.error("Error enumerating devices without permission:", err)
          );
      }
    };
    fetchDevices();
  }, [isMobile]);

  const audioInputs = devices.filter((device) => device.kind === "audioinput");
  const videoInputs = devices.filter((device) => device.kind === "videoinput");

  const handleCameraCustomDropdownToggle = () => {
    setCameraCustomDropdownOpen((prev) => !prev);
    setMicCustomDropdownOpen(false);
  };

  const handleMicCustomDropdownToggle = () => {
    setMicCustomDropdownOpen((prev) => !prev);
    setCameraCustomDropdownOpen(false);
  };

  const handleMobileCameraSwitch = () => {
    const newFacingMode = currentFacingMode === "user" ? "environment" : "user";
    setCurrentFacingMode(newFacingMode);
    if (onFacingModeChange) {
      onFacingModeChange(newFacingMode);
    }
  };

  // Effect to handle closing dropdowns on outside clicks
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (micDropdownRef.current && !micDropdownRef.current.contains(event.target as Node)) {
        setMicCustomDropdownOpen(false);
      }
      if (cameraDropdownRef.current && !cameraDropdownRef.current.contains(event.target as Node)) {
        setCameraCustomDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Update the microphone icon based on the selected device
  useEffect(() => {
    const currentDevice = audioInputs.find(d => d.deviceId === selectedMic);
    if (currentDevice) {
      setSelectedMicIcon(getMicIconFromLabel(currentDevice.label));
    }
  }, []);



  return (
    <div className="flex justify-center items-end md:space-x-4 space-x-2 xsm:space-x-1 flex-grow md:mb-4 mb-2 ">
      <div className="flex items-center md:pr-2 pr-1 bg-blue-50 rounded-2xl relative" ref={micDropdownRef}>
        <Button
          variant="ghost"
          size="icon"
          className="bg-blue-200 text-black cursor-pointer hover:bg-[#fdc300] rounded-2xl h-12 w-12"
          onClick={onToggleMic}
        >
          {isMicOn ? (
            selectedMicIcon
          ) : (
            <MicOff className="h-6 w-6 text-red-500" />
          )}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 bg-blue-50 text-black rounded-full hover:bg-blue-100 transition-colors"
          onClick={handleMicCustomDropdownToggle}
          aria-label="Select microphone device"
        >
          {micCustomDropdownOpen ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </Button>
        {micCustomDropdownOpen && (
          <div className="absolute bottom-full mb-2 md:right-0 bg-white border border-gray-200 rounded-md shadow-lg z-50 min-w-[200px] py-1">
            {audioInputs.length === 0 ? (
              <div className="px-4 py-2 text-gray-500 text-sm">
                No microphones found
              </div>
            ) : (
              <ul className="list-none p-0 m-0">
                {audioInputs.map((device) => (
                  <li
                    key={device.deviceId}
                    onClick={() => {
                      onDeviceChange(device.deviceId, "audio");
                      setMicCustomDropdownOpen(false);
                    }}
                    className={`px-4 py-2 text-black cursor-pointer hover:bg-gray-100 text-sm ${selectedMic === device.deviceId
                      ? "bg-blue-100 font-semibold"
                      : ""
                      }`}
                  >
                    {getMicIconFromLabel(device.label)}
                    <span>
                      {device.label || `Microphone ${device.deviceId.substring(0, 4)}...`}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center md:pr-2 pr-1 bg-blue-50 rounded-2xl relative" ref={cameraDropdownRef}>
        <Button
          variant="ghost"
          size="icon"
          className="bg-blue-200 text-black cursor-pointer hover:bg-[#fdc300] rounded-2xl h-12 w-12"
          onClick={onToggleVideo}
        >
          {isVideoOn ? (
            <Video className="h-6 w-6" />
          ) : (
            <VideoOff className="h-6 w-6 text-red-500" />
          )}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 bg-blue-50 text-black rounded-full hover:bg-blue-100 transition-colors"
          onClick={isMobile ? handleMobileCameraSwitch : handleCameraCustomDropdownToggle}
          aria-label="Select camera device"
        >
          {isMobile ? (
            <div className="">
              <SwitchCamera className="h-4 w-4" />
            </div>
          ) : (
            <div>
              {cameraCustomDropdownOpen ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </div>
          )}
        </Button>
        {cameraCustomDropdownOpen && !isMobile && (
          <div className="absolute bottom-full mb-2 md:right-0 bg-white border border-gray-200  rounded-md shadow-lg z-50 min-w-[200px] py-1">
            {videoInputs.length === 0 ? (
              <div className="px-4 py-2 text-gray-500 text-sm">
                No cameras found
              </div>
            ) : (
              <ul className="list-none p-0 m-0">
                {videoInputs.map((device) => (
                  <li
                    key={device.deviceId}
                    onClick={() => {
                      onDeviceChange(device.deviceId, "video");
                      setCameraCustomDropdownOpen(false);
                    }}
                    className={`px-4 py-2 text-black cursor-pointer hover:bg-gray-100 text-sm ${selectedCamera === device.deviceId
                      ? "bg-blue-100 font-semibold"
                      : ""
                      }`}
                  >
                    {device.label ||
                      `Camera ${device.deviceId.substring(0, 4)}...`}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <AlertDialog >
        <AlertDialogTrigger asChild>
          <Button className="bg-red-500 text-white rounded-full h-12 w-12 hover:bg-red-600 cursor-pointer"
          ><Phone /></Button>
        </AlertDialogTrigger>
        <AlertDialogContent className="bg-white text-black border-0">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will end your call.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-blue-500 text-white">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-500 text-white" onClick={onEndCall} >Continue</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Button
        className="bg-blue-50 text-black md:block hidden cursor-pointer hover:bg-[#fdc300] rounded-full h-12 w-12"
        onClick={onToggleScreenShare}
      >
        {isScreenSharing ? (
          <Monitor className="h-6 w-6 ml-1" />
        ) : (
          <MonitorOff className="h-6 w-6 ml-1" />
        )}
      </Button>

      <div className="relative">
        <Button
          className="bg-blue-50 text-black rounded-full h-12 w-12 hover:bg-[#fdc300] cursor-pointer"
          onClick={onOpenChat}
        >
          <MessageCircleIcon />

        </Button>
        {unreadMessageCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-[#0094ff] text-white text-xs font-bold rounded-full h-4 w-4 flex items-center justify-center animate-bounce">
            {unreadMessageCount}
          </span>
        )}
      </div>
    </div >
  );
};

export default CallControlsButton;